import './KbArticleEdit.css'
import { PUBLIC_WEBSITE } from '@/utilities/envVariables'
import { useApi } from '@/hooks/useApi'
import { routes } from '@/utilities/routes'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { kbArticleDeleteSpec, kbArticleGetSpec, kbArticleSaveSpec, type KbArticleRow } from 'aevocam-rest-api-spec'
import { KbArticleEditAssets } from './KbArticleEditAssets'
import { KbContentDiff } from './KbContentDiff'
import { BasicPage } from '@/components/layout/BasicPage'
import { TextBox } from '@/components/scalar/TextBox'
import { TextArea } from '@/components/scalar/TextArea'
import { Button } from '@/components/action/Button'
import { toastError, toastInfo, toastSuccess, toastWarning } from '@/components/action/Toaster'
import { MarkdownViewer } from '@/components/scalar/MarkdownViewer'
import { AgentChat } from '@/components/agentChat/AgentChat'
import { Icon } from '@/components/graphics/Icon'
import { cn } from '@/utilities/misc'
import { createGlobalState } from '@/utilities/createGlobalState'

type ContentTab = 'draft' | 'published'
type PreviewTab = 'preview' | 'differences'

const NEW_ARTICLE_TEMPLATE = 'Hi there!'

type KbArticleEditState = {
  agentOpen: boolean
}

const usePageState = createGlobalState<KbArticleEditState>('kbArticleEditState', {
  agentOpen: false,
})

export function KbArticleEdit() {
  const { id } = useParams<{ id: 'new' | string }>()
  const [queryParams] = useSearchParams()
  const location = useLocation()
  const api = useApi({ requireLogin: true, adminOnly: true })
  const navigate = useNavigate()

  const [pageState, setPageState] = usePageState()
  const { agentOpen } = pageState

  const [article, setArticle] = useState<KbArticleRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [path, setPath] = useState('')
  const [title, setTitle] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [published, setPublished] = useState(false)
  const [publishAt, setPublishAt] = useState('')
  const [unpublishAt, setUnpublishAt] = useState('')
  const [contentPublic, setContentPublic] = useState('')
  const [contentDraft, setContentDraft] = useState<string | null>(null)
  const [workingDraft, setWorkingDraft] = useState('')
  const [contentTab, setContentTab] = useState<ContentTab>('draft')
  const [previewTab, setPreviewTab] = useState<PreviewTab>('preview')

  const showVersionControls = id !== 'new'

  const savedDraftText = contentDraft ?? contentPublic
  const versionsDiverged = workingDraft !== contentPublic
  const hasSavedDraft = contentDraft != null && contentDraft !== contentPublic
  const hasPendingDraft = versionsDiverged || hasSavedDraft

  const isMetadataDirty = useMemo(() => {
    if (!article) return false
    return (
      path !== (article.path ?? '')
      || title !== (article.title ?? '')
      || subtitle !== (article.subtitle ?? '')
      || publishAt !== (article.publishAt ? String(article.publishAt) : '')
      || unpublishAt !== (article.unpublishAt ? String(article.unpublishAt) : '')
    )
  }, [article, path, title, subtitle, publishAt, unpublishAt])

  const isContentDirty = workingDraft !== savedDraftText
  const isDirty = isMetadataDirty || isContentDirty

  useEffect(() => {
    if (!hasPendingDraft && previewTab === 'differences') {
      setPreviewTab('preview')
    }
  }, [hasPendingDraft, previewTab])

  const loadArticle = useCallback((value: KbArticleRow, options?: {
    /** Keep draft/published and preview/differences tab selection */
    preserveView?: boolean
  }) => {
    const pub = value.contentPublic ?? ''
    const draft = value.contentDraft ?? null
    setArticle(value)
    setPath(value.path ?? '')
    setTitle(value.title ?? '')
    setSubtitle(value.subtitle ?? '')
    setPublished(value.published ?? false)
    setPublishAt(value.publishAt ? String(value.publishAt) : '')
    setUnpublishAt(value.unpublishAt ? String(value.unpublishAt) : '')
    setContentPublic(pub)
    setContentDraft(draft)
    setWorkingDraft(draft ?? pub)
    if (!options?.preserveView) {
      setContentTab('draft')
      setPreviewTab('preview')
    }
  }, [])

  const fetchArticle = useCallback(async (options?: {
    quietly?: boolean
    pathQuery?: string | null
  }) => {
    if (!id || id === 'new') return false

    if (!options?.quietly) setLoading(true)

    try {
      const res = await api.request(kbArticleGetSpec, {
        path: { article: id },
        query: {
          forAdmin: 'true',
          path: options?.pathQuery ?? undefined,
        },
      })

      if (res.status === 200 && res.data?.article) {
        const nextArticle = res.data.article
        loadArticle(nextArticle, { preserveView: options?.quietly })
        const editUrl = routes.adminKbArticleEdit(String(nextArticle.id))
        if (editUrl !== location.pathname) {
          navigate(editUrl, { replace: true })
        }
        return true
      }

      toastError(res.error ?? 'Failed to load article')
      return false
    } catch {
      toastError('Failed to load article')
      return false
    } finally {
      if (!options?.quietly) setLoading(false)
    }
  }, [api, id, loadArticle, location.pathname, navigate])

  const pendingAgentRefreshRef = useRef(false)

  function handleAgentRefresh() {
    if (!pendingAgentRefreshRef.current) return
    pendingAgentRefreshRef.current = false
    void fetchArticle({ quietly: true }).then(ok => {
      if (ok) toastInfo('Article contents have changed')
    })
  }

  useEffect(() => {
    async function fn() {
      if (id === 'new') {

        const article: KbArticleRow = {
          id: -1n,
          path: '/some-article',
          title: 'New article',
          subtitle: 'New article subtitle',
          published: false,
          publishAt: null,
          unpublishAt: null,
          retiredAt: null,
          contentPublic: '',
          contentDraft: null,
        }
        loadArticle(article)
        setWorkingDraft(NEW_ARTICLE_TEMPLATE)
        setLoading(false)
        return
      }  // id === 'new'

      await fetchArticle({ pathQuery: queryParams.get('path') })
    }
    void fn()
  }, [id])

  function normalizeDraft(draft: string, publicText: string): string | null {
    return draft === publicText ? null : draft
  }

  function buildArticleBody(overrides: {
    contentPublic?: string
    contentDraft?: string | null
    published?: boolean
    retiredAt?: Date | string | null
  }): KbArticleRow {
    return {
      ...article!,
      path: path || null,
      title: title || null,
      subtitle: subtitle || null,
      published: overrides.published ?? published,
      publishAt: publishAt || null,
      unpublishAt: unpublishAt || null,
      retiredAt: overrides.retiredAt !== undefined
        ? overrides.retiredAt
        : (article!.retiredAt ?? null),
      contentPublic: overrides.contentPublic ?? contentPublic,
      contentDraft: overrides.contentDraft !== undefined
        ? overrides.contentDraft
        : normalizeDraft(workingDraft, overrides.contentPublic ?? contentPublic),
    }
  }

  async function persistArticle(
    body: KbArticleRow,
    options?: {
      successMessage?: ReactNode
      /** When set, keep this published flag if the server returns something else */
      expectedPublished?: boolean
    },
  ) {
    if (!id || !article) return false

    const res = await api.request(kbArticleSaveSpec, {
      path: { article: String(article.id) },
      body: { article: body },
    })

    if (res.status === 200 && res.data?.article) {
      let savedArticle = res.data.article
      if (
        options?.expectedPublished !== undefined
        && savedArticle.published !== options.expectedPublished
      ) {
        savedArticle = { ...savedArticle, published: options.expectedPublished }
      }
      loadArticle(savedArticle)
      const newUrl = routes.adminKbArticleEdit(String(res.data.article.id))
      if (id === 'new') {
        toastSuccess(options?.successMessage ?? 'New article saved')
        navigate(newUrl)
      } else {
        toastSuccess(options?.successMessage ?? 'Saved')
        window.history.pushState({}, '', newUrl)
      }
      return true
    }

    toastError(res.error ?? 'Failed to save')
    return false
  }

  async function handleSaveDraft() {
    const publishStatus = published
    const draftToSave = normalizeDraft(workingDraft, contentPublic)
    const savedAsDraft = draftToSave != null
    await persistArticle(
      buildArticleBody({
        contentDraft: draftToSave,
        published: publishStatus,
      }),
      {
        expectedPublished: publishStatus,
        successMessage: savedAsDraft
          ? (publishStatus ? 'Draft saved' : 'Saved — not published yet')
          : 'Saved',
      },
    )
  }

  function reloadSavedFields() {
    if (!article) return

    const pub = article.contentPublic ?? ''
    const draft = article.contentDraft ?? null

    setPath(article.path ?? '')
    setTitle(article.title ?? '')
    setSubtitle(article.subtitle ?? '')
    setPublishAt(article.publishAt ? String(article.publishAt) : '')
    setUnpublishAt(article.unpublishAt ? String(article.unpublishAt) : '')
    setPublished(article.published ?? false)

    if (id === 'new' && draft == null && pub === '') {
      setWorkingDraft(NEW_ARTICLE_TEMPLATE)
    } else {
      setWorkingDraft(draft ?? pub)
    }
  }

  function handleDiscard() {
    if (!isDirty) return
    if (!confirm('Discard unsaved changes?')) return

    reloadSavedFields()
    toastInfo('Unsaved changes discarded')
  }

  async function handleRevert() {
    if (!hasSavedDraft || id === 'new') return
    if (!confirm(
      'Discard the saved draft and revert to the published version?\n\n'
      + 'The published article will stay as it is. This cannot be undone.',
    )) return

    await persistArticle(
      buildArticleBody({
        contentDraft: null,
        contentPublic,
        published,
      }),
      {
        successMessage: 'Reverted to published version',
        expectedPublished: published,
      },
    )
  }

  async function handlePublish() {
    if (!confirm('Publish the current draft to the live site?')) return

    const publicText = workingDraft
    await persistArticle(
      buildArticleBody({
        contentPublic: publicText,
        contentDraft: null,
        published: true,
      }),
      { successMessage: 'Published' },
    )
  }

  async function handleUnpublish() {
    if (!confirm('Unpublish this article? It will no longer be visible on the public site.')) return

    await persistArticle(
      buildArticleBody({ published: false }),
      { successMessage: 'Unpublished' },
    )
  }

  async function handleRetire() {
    if (!confirm('Retire this article? It will be hidden from normal listings.')) return

    await persistArticle(
      buildArticleBody({ retiredAt: new Date().toISOString() }),
      { successMessage: 'Retired' },
    )
  }

  async function handleUnretire() {
    if (!confirm('Un-retire this article? It will appear in normal listings again.')) return

    await persistArticle(
      buildArticleBody({ retiredAt: null }),
      { successMessage: 'Un-retired' },
    )
  }

  function handleView() {
    const articlePath = (path || article?.path || '').trim()
    if (!articlePath) return
    let url = PUBLIC_WEBSITE + articlePath
    if (!published || hasSavedDraft)
      url += '?preview'
    window.open(url, '_blank')
  }

  async function handleDelete() {
    if (!id || id === 'new' || !article) return
    if (!confirm('Delete this article?')) return

    const res = await api.request(kbArticleDeleteSpec, {
      path: { article: id },
    })
    if (res.status === 200) {
      toastWarning('Deleted')
      navigate(routes.adminKbArticles())
    } else {
      toastError(res.error ?? 'Failed to delete')
    }
  }

  const editorValue = showVersionControls
    ? (contentTab === 'draft' ? workingDraft : contentPublic)
    : workingDraft

  const editorReadOnly = showVersionControls && contentTab === 'published'

  const previewMarkdown = showVersionControls
    ? (contentTab === 'draft' ? workingDraft : contentPublic)
    : workingDraft

  const markdownViewerProps = {
    articleId: article?.id,
    withinEditor: true as const,
    allowHtml: true as const,
    linksToNewTabs: true as const,
    localLinksTo: routes.adminKbArticleEdit('0') + '?path=',
    headersStartLevel: 2 as const,
  }

  function renderPreviewPane() {
    if (showVersionControls && previewTab === 'differences') {
      const diffNewText = versionsDiverged ? workingDraft : (contentDraft ?? workingDraft)
      return (
        <KbContentDiff
          oldText={contentPublic}
          newText={diffNewText}
        />
      )
    }

    return (
      <MarkdownViewer
        value={previewMarkdown}
        {...markdownViewerProps}
        allowPlugins={showVersionControls && contentTab === 'draft'}
      />
    )
  }

  if (loading) return <main><p>Loading...</p></main>
  if (!article) return <main><p>Article not found</p></main>

  const showSaveDraft = isDirty
  const showDiscard = isDirty
  const showRevert = showVersionControls && hasSavedDraft
  const showPublish = hasPendingDraft || !published
  const showUnpublish = published
  const isRetired = !!article.retiredAt
  const showRetire = id !== 'new' && !isRetired
  const showUnretire = id !== 'new' && isRetired

  return (
    <BasicPage
      title={title ? 'Article: ' + title : 'Edit article'}
      className='KbArticleEdit'
      toolbarButtons={<>
        <Button
          label='Articles'
          icon='chevronLeft'
          href={routes.adminKbArticles()}
        />
        <Button
          label='New article'
          icon='plus'
          disabled={id === 'new'}
          href={routes.adminKbArticleNew()}
          onClick={() => toastInfo('New article')}
        />
        {showSaveDraft && (
          <Button
            variant='primary'
            label='Save draft'
            icon='save'
            onClick={handleSaveDraft}
          />
        )}
        {showDiscard && (
          <Button
            label='Discard'
            icon='times'
            tooltip='Discard unsaved changes'
            onClick={handleDiscard}
          />
        )}
        {showRevert && (
          <Button
            label='Revert'
            icon='undo'
            tooltip='Discard the saved draft and revert to the published version'
            onClick={handleRevert}
          />
        )}
        {showPublish && (
          <Button
            variant={isRetired ? 'secondary' : 'primary'}
            label='Publish'
            icon='checkCircle'
            onClick={handlePublish}
          />
        )}
        {showUnpublish && (
          <Button
            label='Unpublish'
            icon='lock'
            onClick={handleUnpublish}
          />
        )}
        {showRetire && (
          <Button
            label='Retire'
            icon='mdAssistWalker'
            onClick={handleRetire}
          />
        )}
        {showUnretire && (
          <Button
            variant='primary'
            label='Un-retire'
            icon='mdAssistWalker'
            onClick={handleUnretire}
          />
        )}
        <Button
          label='Delete'
          icon='trash'
          disabled={id === 'new'}
          onClick={handleDelete}
        />
        {id !== 'new' && (
          <Button
            label='View'
            icon='monitorPlay'
            tooltip='Opens a new tab'
            onClick={handleView}
          />
        )}
      </>}
    >

      <div className='WorkArea'>

        <div className='Cutout Wrapped'>
          <TextBox
            label='Path'
            tooltip='/some-folder/and-another/the-article'
            required
            width={450}
            value={path}
            setValue={setPath}
          />
          <TextBox
            label='Title'
            required
            width={450}
            value={title}
            setValue={setTitle}
          />
          <TextBox
            label='Subtitle'
            width={450}
            value={subtitle}
            setValue={setSubtitle}
          />
          {/*
          <TextBox
            label='Publish at'
            tooltip={`When to switch 'Published' on`}
            value={publishAt}
            setValue={setPublishAt}
          />
          <TextBox
            label='Unpublish at'
            tooltip={`When to switch 'Published' off`}
            value={unpublishAt}
            setValue={setUnpublishAt}
          />
          */}
        </div>

        {id !== 'new' && (article?.id ?? 0) > 0 && (
          <div className='Cutout'>
            <KbArticleEditAssets articleId={article.id!} />
          </div>
        )}

        <div className='Cutout FlexColumn Gap2'>

          <div className='EditAndView'>

            <div className='Editor'>
              {showVersionControls && (
                <div className='PaneTabs' role='tablist' aria-label='Content source'>
                  <button
                    type='button'
                    role='tab'
                    aria-selected={contentTab === 'draft'}
                    className={cn('Tab', contentTab === 'draft' && 'Active', hasPendingDraft && 'Diverged')}
                    onClick={() => setContentTab('draft')}
                  >
                    Draft
                    {hasPendingDraft && <span className='Indicator' aria-hidden />}
                  </button>
                  <button
                    type='button'
                    role='tab'
                    aria-selected={contentTab === 'published'}
                    className={cn('Tab', contentTab === 'published' && 'Active', hasPendingDraft && 'Diverged')}
                    onClick={() => setContentTab('published')}
                  >
                    Published
                    {hasPendingDraft && <span className='Indicator' aria-hidden />}
                  </button>
                </div>
              )}
              <TextArea
                autoHeight
                monospace
                spellCheck
                readOnly={editorReadOnly}
                value={editorValue}
                setValue={editorReadOnly ? undefined : setWorkingDraft}
              />
            </div>

            <div className='Viewer'>
              {showVersionControls && (
                <div className='PaneTabs' role='tablist' aria-label='Preview mode'>
                  <button
                    type='button'
                    role='tab'
                    aria-selected={previewTab === 'preview'}
                    className={cn('Tab', previewTab === 'preview' && 'Active')}
                    onClick={() => setPreviewTab('preview')}
                  >
                    Preview
                  </button>
                  <button
                    type='button'
                    role='tab'
                    aria-selected={previewTab === 'differences'}
                    className={cn(
                      'Tab',
                      previewTab === 'differences' && 'Active',
                      hasPendingDraft && 'Diverged',
                    )}
                    onClick={() => setPreviewTab('differences')}
                    disabled={!hasPendingDraft}
                  >
                    Differences
                    {hasPendingDraft && <span className='Indicator' aria-hidden />}
                  </button>
                </div>
              )}
              {renderPreviewPane()}
            </div>

          </div>

          <div className='ExtraViewer'>
            {showVersionControls && (
              <div className='PaneTabs' role='tablist' aria-label='Preview mode'>
                <button
                  type='button'
                  role='tab'
                  aria-selected={previewTab === 'preview'}
                  className={cn('Tab', previewTab === 'preview' && 'Active')}
                  onClick={() => setPreviewTab('preview')}
                >
                  Preview
                </button>
                <button
                  type='button'
                  role='tab'
                  aria-selected={previewTab === 'differences'}
                  className={cn(
                    'Tab',
                    previewTab === 'differences' && 'Active',
                    hasPendingDraft && 'Diverged',
                  )}
                  onClick={() => setPreviewTab('differences')}
                  disabled={!hasPendingDraft}
                >
                  Differences
                  {hasPendingDraft && <span className='Indicator' aria-hidden />}
                </button>
              </div>
            )}
            {renderPreviewPane()}
          </div>

        </div>

      </div>

      <aside className={cn('AgentRail', agentOpen && 'Open')}>
        <div className='RailPanel Cutout'>
          <button
            type='button'
            className='RailExpand'
            aria-expanded={agentOpen}
            aria-label='Open agent'
            onClick={() => setPageState(prev => ({ ...prev, agentOpen: true }))}
          >
            <Icon icon='riAiAgentFill' />
            <span className='Label'>Agent</span>
          </button>
          <div className='RailBody'>
            <AgentChat
              agent='kb'
              width='100%'
              persistenceKey='kbEdit'
              onHide={() => setPageState(prev => ({ ...prev, agentOpen: false }))}
              onRefreshEditor={() => { pendingAgentRefreshRef.current = true }}
              onReplyDone={handleAgentRefresh}
              context={{
                page: 'articleEdit',
                articleId: article?.id != null ? String(article.id) : undefined,
                path: path || undefined,
                title: title || undefined,
              }}
            />
          </div>
        </div>
      </aside>

    </BasicPage>
  )
}
